import { EnvironmentProviders, isDevMode, Provider } from '@angular/core';
import { provideTransloco } from '@jsverse/transloco';
import { LanguagesSystem } from '../constants';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  httpAuthInterceptor,
  httpCacheInterceptor,
  httpErrorInterceptor,
} from '../interceptors';
import { LocalStorageEnum, TranslocoHttpLoader } from '@olymp/shared';

export function provideCore(): (EnvironmentProviders | Provider)[] {
  const lang = LanguagesSystem;
  return [
    provideTransloco({
      config: {
        availableLangs: (Object.keys(lang) as Array<keyof typeof lang>).map(
          (key) => ({
            label: key,
            id: lang[key],
          }),
        ),
        defaultLang: setActiveLang(),
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        // Tarjimasi yo'q kalit uchun dev konsolga "Missing translation" yozilmasin:
        // ko'p joyda kalit bo'lmasa o'zbekcha fallback ishlatiladi va konsol
        // socket/progress loglarini ko'rsatmay qoladi.
        missingHandler: { logMissingKey: false },
      },
      loader: TranslocoHttpLoader,
    }),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: definePreset(Aura, {
          components: {
            inputtext: {
              background: 'white',
            },
            button: {
              border: {
                radius: '12px',
              },
              css: () => `
               .p-button{
                 height: 48px !important;
               }
              `,
              primary: {
                background: '#28C76F !important',
                border: {
                  color: '#28C76F',
                },
                hover: {
                  background: '#24B365 !important',
                  border: {
                    color: '#24B365 !important',
                  },
                },
                active: {
                  background: '#1E9957 !important',
                  border: {
                    color: '#1E9957 !important',
                  },
                },
              },
              secondary: {
                background: '#D6DCED !important',
                border: {
                  color: '#D6DCED',
                },
                hover: {
                  background: '#C3C9DC !important',
                  border: {
                    color: '#C3C9DC !important',
                  },
                },
                active: {
                  background: '#B0B7CB !important',
                  border: {
                    color: '#B0B7CB !important',
                  },
                },
              },
              info: {
                background: '#2E90FA !important',
                border: {
                  color: '#2E90FA',
                },
                hover: {
                  background: '#297FE0 !important',
                  border: {
                    color: '#297FE0 !important',
                  },
                },
                active: {
                  background: '#216DBF !important',
                  border: {
                    color: '#216DBF !important',
                  },
                },
              },
              help: {
                background: '#FFBE2C !important',
                border: {
                  color: '#FFBE2C !important',
                },
                hover: {
                  background: '#E6AB28 !important',
                  border: {
                    color: '#E6AB28 !important',
                  },
                },
                active: {
                  background: '#CC9723 !important',
                  border: {
                    color: '#CC9723 !important',
                  },
                },
              },
            },
            datepicker: {
              panel: {
                border: {
                  radius: '8px',
                },
              },
            },
            datatable: {
              css: () => `
                .p-datatable {
                    font-size: 14px;
                }
                .p-datatable .p-datatable-thead > tr > th:first-child {
                    border-top-left-radius: 12px;
                }
                .p-datatable .p-datatable-thead > tr > th:last-child {
                border-top-right-radius: 12px;
                }

                .p-datatable .p-datatable-tbody > tr:last-child > td:first-child {
                    border-bottom-left-radius: 12px;
                }
                  .p-datatable .p-datatable-tbody > tr:last-child > td:last-child {
                    border-bottom-right-radius: 12px;
                }

                .p-datatable-tbody > tr > td, .p-datatable-thead > tr > th {
                  border: 0 !important;
                }
                .p-datatable-tbody > tr:nth-child(odd)
                {
                  background: #f7f9fc;
                }
                .p-datatable-thead > tr > th, .p-datatable-tbody > tr > td
                {
                  color: #4F5662;
                }
              `,
            },
            paginator: {
              paginator: {
                nav: {
                  button: {
                    selected: {
                      background: 'red',
                    },
                  },
                },
              },
              css: () => `
                .p-paginator-prev, .p-paginator-next, .p-paginator-page {
                  width: 40px;
                  height: 40px;
                  border-radius: 12px;
                  font-size: 14px;
                  background: white;
                  color: #4F5662;
                  font-weight: 500;
                }
                .p-paginator-page-selected
                {
                  background: #2E90FA !important;
                  color: white !important;
                }
              `,
            },
          },
        }),
        options: {
          darkModeSelector: '.dark',
        },
      },
    }),
    provideHttpClient(
      withInterceptors([
        httpCacheInterceptor,
        httpAuthInterceptor,
        httpErrorInterceptor,
      ]),
    ),
  ];
}

export function setActiveLang(): LanguagesSystem {
  const lang = localStorage.getItem(LocalStorageEnum.Language);
  if (Object.values(LanguagesSystem).includes(lang as LanguagesSystem)) {
    return lang as LanguagesSystem;
  }
  return 'uz' as LanguagesSystem;
}
